import React from 'react';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RedditIcon from '@mui/icons-material/Reddit';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SocialMediaPostProps } from './types';
import { TwitterPost, FacebookPost } from './CorePlatforms';
import { InstagramPost, LinkedInPost } from './SocialMediaPost';
import { RedditPost } from './AdditionalPlatforms';
import { YouTubePost, TikTokPost } from './ExtendedPlatforms';

// Get platform-specific styling and icons
export const getPlatformIcon = (platform?: string) => {
  switch (platform?.toLowerCase()) {
    case 'twitter':
    case 'x':
      return <TwitterIcon />;
    case 'facebook':
      return <FacebookIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    case 'reddit':
      return <RedditIcon />;
    case 'youtube':
      return <YouTubeIcon />;
    default:
      return <TwitterIcon />;
  }
};

// Get platform-specific styling
export const getPlatformStyle = (platform?: string) => {
  switch (platform?.toLowerCase()) {
    case 'twitter':
    case 'x':
      return { 
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#15202b' : '#ffffff',
        borderRadius: 2,
        border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? '#2f3336' : '#eff3f4'}`
      };
    case 'facebook':
      return { 
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#242526' : '#ffffff',
        borderRadius: 2,
        border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? '#3E4042' : '#dddfe2'}`
      };
    case 'instagram':
      return { 
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#000000' : '#ffffff',
        borderRadius: 0,
        border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? '#262626' : '#dbdbdb'}`
      };
    case 'linkedin':
      return { 
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#1b1f23' : '#ffffff',
        borderRadius: 2,
        border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? '#38434f' : '#e0e0e0'}`
      };
    default:
      return { 
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#15202b' : '#ffffff',
        borderRadius: 2,
        border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? '#2f3336' : '#eff3f4'}`
      };
  }
};

// Platform renderer component that selects the appropriate platform post
export const PlatformRenderer: React.FC<SocialMediaPostProps> = ({ 
  platform, 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  notableDeviations, 
  replies7d, 
  likes7d 
}) => {
  const platformLower = platform?.toLowerCase();
  
  const commonProps = {
    content,
    sender,
    displayImageUrl,
    altText,
    notableDeviations,
    replies7d,
    likes7d
  };
  
  switch (platformLower) {
    case 'facebook':
      return <FacebookPost {...commonProps} />;
    case 'instagram':
      return <InstagramPost {...commonProps} />;
    case 'linkedin':
      return <LinkedInPost {...commonProps} />;
    case 'reddit':
      return <RedditPost {...commonProps} />;
    case 'youtube':
      return <YouTubePost {...commonProps} />;
    case 'tiktok':
      return <TikTokPost {...commonProps} />;
    case 'twitter':
    case 'x':
    default:
      return <TwitterPost {...commonProps} />;
  }
};